/**
 * Announcements mixin
 */
import { ANNOUNCEMENT_TYPES, INVITE_STATUSES } from "@/helpers/constants";
import { mapGetters } from "vuex";
import dayjs from "dayjs";

export default {
  computed: {
    ...mapGetters({
      groupTerminology: "room/groupTerminology"
    }),
    /**
     * Announcements config
     * @return {Object}
     */
    announcementsConfig() {
      return this.$store.state.event.config.defaultAnnouncementsConfig;
    },
    /**
     * Banner's headers
     * @return {Object}
     */
    systemHeaders() {
      return this.$store.getters["config/systemHeaders"];
    },
    /**
     * Banner's messages
     * @return {Object}
     */
    systemMessages() {
      return this.$store.getters["config/systemMessages"];
    },

    /**
     * Current announce
     * @return {Object}
     */
    announce() {
      return this.$store.state.socket.announce;
    },

    /**
     * Map between announce type and banner header text
     * @return {Object}
     */
    announceHeaderMap() {
      let announceHeaderMap = {};

      announceHeaderMap[ANNOUNCEMENT_TYPES.BANNER] = this.systemHeaders.ANNOUNCE;
      announceHeaderMap[ANNOUNCEMENT_TYPES.CELEBRATION] = this.systemHeaders.ANNOUNCE;
      announceHeaderMap[
        ANNOUNCEMENT_TYPES.TABLE_MOVEMENT_INVITE_SEND
      ] = this.systemHeaders.TABLE_MOVEMENT_INVITE.format(this.groupTerminology);
      announceHeaderMap[
        ANNOUNCEMENT_TYPES.TABLE_MOVEMENT_INVITE_ACCEPT
      ] = this.systemHeaders.TABLE_MOVEMENT_INVITE.format(this.groupTerminology);
      announceHeaderMap[
        ANNOUNCEMENT_TYPES.TABLE_MOVEMENT_INVITE_DECLINE
      ] = this.systemHeaders.TABLE_MOVEMENT_INVITE.format(this.groupTerminology);
      announceHeaderMap[ANNOUNCEMENT_TYPES.VIDEO_CALL_INVITE_SEND] = this.systemHeaders.VIDEO_CALL_INVITE;
      announceHeaderMap[ANNOUNCEMENT_TYPES.VIDEO_CALL_INVITE_ACCEPT] = this.systemHeaders.VIDEO_CALL_INVITE;
      announceHeaderMap[ANNOUNCEMENT_TYPES.VIDEO_CALL_INVITE_DECLINE] = this.systemHeaders.VIDEO_CALL_INVITE;
      announceHeaderMap[ANNOUNCEMENT_TYPES.ONE_TO_ONE_VIDEO_CALL_ENDED] = this.systemHeaders.VIDEO_CALL;
      announceHeaderMap[ANNOUNCEMENT_TYPES.LEAVE_GROUP_VIDEO_CALL] = this.systemHeaders.VIDEO_CALL;

      return announceHeaderMap;
    }
  },
  methods: {
    /**
     * Show announce to all event`s users
     * @param message {Object}
     */
    showAnnounce(message) {
      let announcementConfig = this.announcementsConfig[message.announceType] || this.announcementsConfig.ANNOUNCE;

      let announceObject = {
        type: "warning",
        multiLine: true,
        hideIcon: true,
        top: true,
        right: true,
        showClose: false,
        closeDelay: null,
        HTMLMessage: `${this.getAnnounceHeader(message)}<p>${this.getAnnounceBody(message)}</p>`
      };

      let announce = this.$notify(
        Object.assign(
          announceObject,
          this.announcementsConfig.ANNOUNCE, // Set default announce config
          announcementConfig, // Extend config with special announce type
          // Add special listener for announce action (for catching action in our code)
          announcementConfig.actionText ? { onActionClick: () => this.hideAnnounce(announce) } : {},
          // Close delay will be always null. Auto-closing will be work via setTimeout (see below)
          { closeDelay: null }
        )
      );

      if (message.timeout > 0) {
        announce.timeoutId = setTimeout(async () => {
          this.hideAnnounce(announce);
        }, message.timeout * 1000);
      }
    },

    /**
     * Get Announce header
     * @param message {Object}
     * @return {String}
     */
    getAnnounceHeader(message) {
      return this.announceHeaderMap[message.announceType];
    },

    /**
     * Get Announce body
     * @param message {Object}
     * @return {String}
     */
    getAnnounceBody(message) {
      let announceBody = null;

      switch (message.announceType) {
        case ANNOUNCEMENT_TYPES.TABLE_MOVEMENT_INVITE_ACCEPT:
          announceBody = this.systemMessages.USER_MOVEMENT_INVITE_ACCEPT.format(
            message.messageBody.inviteeFullName,
            this.groupTerminology.toLowerCase()
          );
          break;

        case ANNOUNCEMENT_TYPES.TABLE_MOVEMENT_INVITE_DECLINE:
          announceBody = this.systemMessages.USER_MOVEMENT_INVITE_DECLINE.format(
            message.messageBody.inviteeFullName,
            this.groupTerminology.toLowerCase()
          );
          break;

        case ANNOUNCEMENT_TYPES.VIDEO_CALL_INVITE_DECLINE:
          switch (message.inviteStatus) {
            case INVITE_STATUSES.DECLINE_BY_INVITEE:
              announceBody = this.systemMessages.VIDEO_CALL_INVITE_DECLINE_BY_INVITEE.format(
                message.messageBody.inviteeFullName
              );
              break;
            case INVITE_STATUSES.DECLINE_BY_ACCEPTING_ANOTHER_CALL:
              announceBody = this.systemMessages.VIDEO_CALL_INVITE_DECLINE_BY_ACCEPTING_ANOTHER_CALL.format(
                message.messageBody.inviteeFullName
              );
              break;
            case INVITE_STATUSES.DECLINE_BY_INVITER:
              announceBody = this.systemMessages.VIDEO_CALL_INVITE_DECLINE_BY_INVITER.format(
                message.messageBody.inviterFullName
              );
              break;
            case INVITE_STATUSES.DECLINE_BY_TIMEOUT:
              announceBody = this.systemMessages.VIDEO_CALL_INVITE_DECLINE_BY_TIMEOUT.format(
                message.messageBody.inviteeFullName
              );
              break;
          }
          break;

        case ANNOUNCEMENT_TYPES.ONE_TO_ONE_VIDEO_CALL_ENDED: {
          let startedAt = dayjs(message.startedAt);
          let endedAt = dayjs(new Date());
          let diff = endedAt.diff(startedAt);
          let duration = dayjs(diff).format(diff > 3600000 ? "hh:mm:ss" : "mm:ss");

          announceBody = this.systemMessages.ONE_TO_ONE_VIDEO_CALL_ENDED.format(message.participantFullName, duration);
          break;
        }

        case ANNOUNCEMENT_TYPES.LEAVE_GROUP_VIDEO_CALL:
          announceBody = this.systemMessages.LEAVE_GROUP_VIDEO_CALL.format(message.messageBody.participantFullName);
          break;

        default:
          announceBody = message.messageBody.replaceAll("\n", "<br/>");
          break;
      }

      return announceBody;
    },

    /**
     * Remove announce from the state
     * @param announce {Object}
     */
    hideAnnounce(announce) {
      this.$store.commit("socket/removeAnnounce");
      announce.close();
      if (announce.timeoutId) {
        clearTimeout(announce.timeoutId);
      }
    }
  }
};
